import { BrowserRouter, Routes, Route, Outlet, useLocation } from "react-router-dom";
import { useEffect, Suspense, lazy } from "react";
import "@/App.css";
import { AuthProvider } from "@/contexts/AuthContext";
import { CartProvider } from "@/contexts/CartContext";
import { SettingsProvider } from "@/contexts/SettingsContext";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import BottomBar from "@/components/BottomBar";
import PromoPopup from "@/components/PromoPopup";
import CookieBanner from "@/components/CookieBanner";
import WelcomePopup from "@/components/WelcomePopup";
import MobileNav from "@/components/MobileNav";

// --- Route-based code splitting ---
// Kiekvienas puslapis (ir VISA admin sekcija) kraunama tik tada, kai lankytojas
// ten realiai patenka, o ne visiems iš karto pirmo apsilankymo metu.
// Layout apvalkalas (Header/Footer/BottomBar/popup'ai) lieka eager, nes reikalingas
// iškart kiekviename viešame puslapyje.
const Home = lazy(() => import("@/pages/Home"));
const Catalog = lazy(() => import("@/pages/Catalog"));
const ProductDetail = lazy(() => import("@/pages/ProductDetail"));
const Cart = lazy(() => import("@/pages/Cart"));
const Checkout = lazy(() => import("@/pages/Checkout"));
const Login = lazy(() => import("@/pages/Login"));
const ForgotPassword = lazy(() => import("@/pages/ForgotPassword"));
const ResetPassword = lazy(() => import("@/pages/ResetPassword"));
const Register = lazy(() => import("@/pages/Register"));
const About = lazy(() => import("@/pages/About"));
const Privacy = lazy(() => import("@/pages/Privacy"));
const Rules = lazy(() => import("@/pages/Rules"));
const Category = lazy(() => import("@/pages/Category"));
const NotFound = lazy(() => import("@/pages/NotFound"));
const FAQ = lazy(() => import("@/pages/FAQ"));
const Shipping = lazy(() => import("@/pages/Shipping"));
const Withdrawal = lazy(() => import("@/pages/Withdrawal"));
const Info = lazy(() => import("@/pages/Info"));
const Picker = lazy(() => import("@/pages/Picker"));
const Ideas = lazy(() => import("@/pages/Ideas"));
const Account = lazy(() => import("@/pages/Account"));
const PaymentSuccess = lazy(() => import("@/pages/PaymentSuccess"));
const PaymentCancelled = lazy(() => import("@/pages/PaymentCancelled"));

const AdminLayout = lazy(() => import("@/pages/admin/AdminLayout"));
const AdminDashboard = lazy(() => import("@/pages/admin/Dashboard"));
const AdminProducts = lazy(() => import("@/pages/admin/Products"));
const AdminContent = lazy(() => import("@/pages/admin/Content"));
const AdminAds = lazy(() => import("@/pages/admin/Ads"));
const AdminOrders = lazy(() => import("@/pages/admin/Orders"));
const AdminCoupons = lazy(() => import("@/pages/admin/Coupons"));
const AdminUsers = lazy(() => import("@/pages/admin/Users"));
const AdminSettings = lazy(() => import("@/pages/admin/Settings"));
const AdminReports = lazy(() => import("@/pages/admin/Reports"));
const AdminSuppliers = lazy(() => import("@/pages/admin/Suppliers"));
const AdminPurchases = lazy(() => import("@/pages/admin/Purchases"));
const AdminManualInvoice = lazy(() => import("@/pages/admin/ManualInvoice"));

function ScrollToTop() {
  const { pathname, hash } = useLocation();
  useEffect(() => {
    if (hash) {
      // Trumpa uzlaida - elementas turi buti sumontuotas DOM'e prieš scrollinant i ji
      const id = hash.replace("#", "");
      const t = setTimeout(() => {
        const el = document.getElementById(id);
        if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
        else window.scrollTo(0, 0);
      }, 80);
      return () => clearTimeout(t);
    }
    window.scrollTo(0, 0);
  }, [pathname, hash]);
  return null;
}

function RouteFallback() {
  // Minimalus, prekės ženklo spalvos krovimo indikatorius — rodomas tik per
  // pirmą konkretaus puslapio chunk'o parsisiuntimą (vėliau naršyklė cache'ina).
  return (
    <div className="flex items-center justify-center py-32" data-testid="route-loading">
      <div className="w-8 h-8 rounded-full border-2 border-[#00B5E2]/25 border-t-[#00B5E2] animate-spin" />
    </div>
  );
}

function PublicLayout() {
  return (
    <>
      <Header />
      <main className="pb-16 md:pb-0"><Outlet /></main>
      <Footer />
      <BottomBar />
      <PromoPopup />
      <CookieBanner />
      <WelcomePopup />
      <MobileNav />
    </>
  );
}

function AppRouter() {
  return (
    <Suspense fallback={<RouteFallback />}>
      <Routes>
        <Route element={<PublicLayout />}>
          <Route path="/" element={<Home />} />
          <Route path="/parduotuve" element={<Catalog />} />
          <Route path="/parinkejas" element={<Picker />} />
          <Route path="/idejos" element={<Ideas />} />
          <Route path="/produktas/:slug" element={<ProductDetail />} />
          <Route path="/filamentai/:slug" element={<Category type="material" />} />
          <Route path="/prekiu-zenklai/:slug" element={<Category type="brand" />} />
          <Route path="/krepselis" element={<Cart />} />
          <Route path="/atsiskaitymas" element={<Checkout />} />
          <Route path="/atsiskaitymas/sekme" element={<PaymentSuccess />} />
          <Route path="/atsiskaitymas/atsaukta" element={<PaymentCancelled />} />
          <Route path="/paskyra" element={<Account />} />
          <Route path="/prisijungti" element={<Login />} />
          <Route path="/pamirsau-slaptazodi" element={<ForgotPassword />} />
          <Route path="/atstatyti-slaptazodi" element={<ResetPassword />} />
          <Route path="/registruotis" element={<Register />} />
          <Route path="/mus" element={<About />} />
          <Route path="/privatumas" element={<Privacy />} />
          <Route path="/taisykles" element={<Rules />} />
          <Route path="/duk" element={<FAQ />} />
          <Route path="/pristatymas" element={<Shipping />} />
          <Route path="/atsisakyti-sutarties" element={<Withdrawal />} />
          <Route path="/info" element={<Info />} />
          <Route path="/info/:section" element={<Info />} />
          <Route path="/info/:section/:slug" element={<Info />} />
          <Route path="*" element={<NotFound />} />
        </Route>
        <Route path="/admin" element={<AdminLayout />}>
          <Route index element={<AdminDashboard />} />
          <Route path="produktai" element={<AdminProducts />} />
          <Route path="turinys" element={<AdminContent />} />
          <Route path="reklama" element={<AdminAds />} />
          <Route path="uzsakymai" element={<AdminOrders />} />
          <Route path="ataskaitos" element={<AdminReports />} />
          <Route path="tiekejai" element={<AdminSuppliers />} />
          <Route path="pirkimai" element={<AdminPurchases />} />
          <Route path="rankine-saskaita" element={<AdminManualInvoice />} />
          <Route path="kuponai" element={<AdminCoupons />} />
          <Route path="vartotojai" element={<AdminUsers />} />
          <Route path="nustatymai" element={<AdminSettings />} />
        </Route>
      </Routes>
    </Suspense>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <SettingsProvider>
        <CartProvider>
          <BrowserRouter>
            <ScrollToTop />
            <AppRouter />
          </BrowserRouter>
        </CartProvider>
      </SettingsProvider>
    </AuthProvider>
  );
}
